'use client';

import { ReactNode, RefCallback, useEffect, useMemo, useState } from 'react';

import { FundingKey } from '@bytel/leviator';
import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    Column,
    Columns,
    Container,
    Icon,
    IconName,
    IconSize,
    Tag,
    Text,
    TextLevels,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull, ProductStockDetails } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import { capitalize } from '@helpers/wording';

import { MarketingType, PlanRange } from '@constants/plan';

import { EasyTradeInCtaState, ProductCTA, SkeletonSticky } from '@product-detail/components';
import { CTASource } from '@product-detail/constants';
import { PricesDetails } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

const getPlanTitle = (type: WallType, plan: MobilePlanType, isRenewal: boolean): string => {
    const baseLabel = type === WallType.HOTSPOT ? 'Internet' : 'Forfait';
    const brandLabel = plan.commercialRange === PlanRange.B_YOU ? 'B&you ' : '';
    const dataLabel = isRenewal ? plan.renewalLabelDataEnvelope : (plan.dataEnvelope ?? '');

    return `${baseLabel} ${brandLabel}${dataLabel}`.trim();
};

export type ProductStickyBoxProps = {
    type: WallType;
    currentProduct: ProductChildFull;
    currentPlan?: MobilePlanType | null;
    openShopAvailabilityModal: ClickEvent;
    isTicketShowed: boolean;
    isRenewal: boolean;
    prices: PricesDetails;
    contract?: ContractType;
    isLoading: boolean;
    showRef: RefCallback<HTMLDivElement>;
    dataCy?: string;
    idPu?: string;
    simulatedPortabilitySelected: boolean;
    easyTradeIn?: EasyTradeInCtaState;
    stockDetails?: ProductStockDetails;
};

export function ProductStickyBox({
    type,
    currentProduct,
    currentPlan,
    openShopAvailabilityModal,
    isTicketShowed,
    isRenewal,
    prices,
    contract,
    isLoading,
    showRef,
    idPu,
    simulatedPortabilitySelected,
    easyTradeIn,
    stockDetails,
}: Readonly<ProductStickyBoxProps>) {
    const { parentTitle } = currentProduct;
    const { productPrices, planPrices, funding } = prices;
    const [stickySentinel, setStickySentinel] = useState<HTMLDivElement | null>(null);
    const [isStuck, setIsStuck] = useState(false);

    useEffect(() => {
        if (!stickySentinel) return;

        const observer = new IntersectionObserver(([entry]) => {
            setIsStuck(!!entry && !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        observer.observe(stickySentinel);
        return () => observer.disconnect();
    }, [stickySentinel]);

    const productIcon = useMemo(() => {
        switch (type) {
            case WallType.PHONE:
            case WallType.SALES_PHONE:
                return IconName.MOBILE;
            case WallType.ACCESSORY:
            case WallType.SALES_ACCESSORY:
                return IconName.ACCESSORIES;
            case WallType.HOTSPOT:
                return IconName.HOTSPOT;
            case WallType.KIDS_WATCH:
                return IconName.WATCH;
            default:
                return IconName.ENERGY;
        }
    }, [type]);

    const generateProductSubtitle = () => {
        if (funding && funding.type !== FundingKey.COMPTANT) {
            return (
                <>
                    {'+ '}
                    <InlinePrice
                        typo={[]}
                        level={TextLevels.THREE}
                        amount={productPrices.fundingMonthlyAmount}
                        period={`mois x${funding.installmentCount} mois`}
                    />
                </>
            );
        }
        if (type !== WallType.HOTSPOT) {
            return `\u00A0`;
        }
    };

    const productPromoPourcent = useMemo(() => {
        if (type === WallType.SALES_PHONE || type === WallType.SALES_ACCESSORY) {
            return (
                currentProduct.details?.discounts.reduce((acc, d) => {
                    if (d.marketingTypes.includes(MarketingType.SALES)) {
                        return acc + d.discount;
                    }
                    return acc;
                }, 0) ?? 0
            );
        }
        return 0;
    }, [type, currentProduct]);

    const productPriceAccessibilityLabel = useMemo(() => {
        let oldPriceLabel = '';
        if (type === WallType.ACCESSORY && productPrices.initialPriceTTC !== productPrices.marketingAmount) {
            oldPriceLabel = `, au lieu de ${productPrices.initialPriceTTC}€`;
        }
        return `${parentTitle} à ${productPrices.marketingAmount}€${oldPriceLabel}`;
    }, [type, parentTitle, productPrices]);

    if (isLoading || productPrices.marketingAmount <= 0) {
        return <SkeletonSticky isTicketShowed={isTicketShowed} type={type} showRef={showRef} />;
    }

    if (isRenewal && currentPlan === undefined) {
        return null;
    }

    return (
        <>
            <div ref={setStickySentinel} className='product-sticky-sentinel' aria-hidden='true' />
            <Container
                className={classNames(
                    'sticky-box product-sticky-box is-sticky-top is-backgroundless',
                    isTicketShowed ? 'hidden' : '',
                    isStuck ? 'is-stuck' : '',
                )}
            >
                <Box className='is-radiusless-mobile'>
                    <Columns mobile verticalAlign={Align.CENTER} marginless>
                        <Column>
                            <View className='gap-huge is-flex-tablet'>
                                <StickyItem
                                    icon={productIcon}
                                    maxWidth={type === WallType.PHONE || type === WallType.HOTSPOT ? '40rem' : 'unset'}
                                    title={
                                        <span aria-label={productPriceAccessibilityLabel}>
                                            {parentTitle} à{' '}
                                            {(type === WallType.SALES_PHONE ||
                                                type === WallType.SALES_ACCESSORY ||
                                                type === WallType.ACCESSORY) &&
                                                productPrices.marketingAmount !== productPrices.initialPriceTTC && (
                                                    <>
                                                        <span className='sr-only'>Ancien prix :</span>
                                                        <InlinePrice
                                                            typo={[]}
                                                            strike
                                                            amount={productPrices.initialPriceTTC}
                                                            data-cy='sticky-striked-product-price'
                                                        />{' '}
                                                        <span className='sr-only'>Prix actuel :</span>
                                                    </>
                                                )}
                                            <InlinePrice
                                                typo={[]}
                                                amount={productPrices.marketingAmount}
                                                data-cy='sticky-product-price'
                                            />
                                            {(type === WallType.SALES_PHONE || type === WallType.SALES_ACCESSORY) &&
                                                productPromoPourcent > 0 && (
                                                    <>
                                                        {' '}
                                                        <Tag label={`-${productPromoPourcent}%`} />
                                                    </>
                                                )}
                                        </span>
                                    }
                                    subtitle={currentPlan ? generateProductSubtitle() : undefined}
                                />
                                {currentPlan && planPrices && (
                                    <StickyItem
                                        icon={IconName.SIM_CARD}
                                        title={
                                            <>
                                                {getPlanTitle(type, currentPlan, isRenewal)} à{' '}
                                                {planPrices.striked > planPrices.final && (
                                                    <>
                                                        <span className='sr-only'>Ancien prix :</span>
                                                        <InlinePrice
                                                            typo={[]}
                                                            strike
                                                            amount={planPrices.striked}
                                                            data-cy='sticky-striked-product-price'
                                                        />{' '}
                                                        <span className='sr-only'>Prix actuel :</span>
                                                    </>
                                                )}
                                                <InlinePrice
                                                    typo={[]}
                                                    amount={planPrices.final}
                                                    data-cy='sticky-plan-final-price'
                                                />
                                            </>
                                        }
                                        subtitle={
                                            currentPlan.isOwned ? (
                                                'Votre forfait actuel'
                                            ) : planPrices.promoDuration && planPrices.promoDuration > 0 ? (
                                                <>
                                                    Pendant {planPrices.promoDuration} mois puis&nbsp;
                                                    <InlinePrice
                                                        typo={[]}
                                                        level={TextLevels.THREE}
                                                        amount={planPrices.forever}
                                                        data-cy='sticky-plan-forever-price'
                                                    />
                                                    &nbsp;- {capitalize(currentPlan.obligationLabel)}
                                                </>
                                            ) : (
                                                capitalize(currentPlan.obligationLabel)
                                            )
                                        }
                                        data-cy='sticky-plan'
                                    />
                                )}
                            </View>
                        </Column>
                        <Column narrow>
                            <ProductCTA
                                type={type}
                                source={CTASource.STICKY}
                                currentProduct={currentProduct}
                                openShopAvailabilityModal={openShopAvailabilityModal}
                                plan={currentPlan}
                                contract={contract}
                                isRenewal={isRenewal}
                                fundingKey={funding?.type}
                                idPu={idPu}
                                simulatedPortabilitySelected={simulatedPortabilitySelected}
                                easyTradeIn={easyTradeIn}
                                stockDetails={stockDetails}
                            />
                        </Column>
                    </Columns>
                </Box>
            </Container>
            <div ref={showRef} />
        </>
    );
}

function StickyItem({
    icon,
    title,
    subtitle,
    maxWidth = '40rem',
    ...props
}: {
    icon: IconName;
    title: ReactNode;
    maxWidth?: string;
    subtitle?: ReactNode;
}) {
    return (
        <View flexable align={Align.CENTER} style={{ maxWidth }} className='gap-medium w-fit' {...props}>
            <Icon name={icon} size={IconSize.MEDIUM} className='is-hidden-tablet-only w-fit' />
            <div>
                <Text marginless typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}>
                    {title}
                </Text>
                {subtitle && (
                    <Text level={TextLevels.THREE} marginless>
                        {subtitle}
                    </Text>
                )}
            </div>
        </View>
    );
}
